import {
    Card, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, InputLabel,
    LinearProgress, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TEXTO_COLORIDO_ESCURO, coresDoTema } from "assets/coresTema";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDPeriodo from "components/MDPeriodo";
import MDSelect from "components/MDSelect";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { useVisualizadorArquivos } from "context/VisualizadorArquivosContext";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import { useCallback, useEffect, useMemo, useState } from "react";
import { get, getFile, post } from "utils/WebService";
import {
    FAROIS, FILTROS_SITUACAO, GRUPOS, SITUACOES, contarGrupos, correspondeBusca, dataCurta, dataHora, dataIso,
    formatarDuracao, formatarTamanho, grupoDoFarol, ordenarRobos, tempoRelativo,
} from "./monitorRobosUtils";

const ATUALIZACAO_OPTIONS = [
    { id: 0, value: 0, label: "Desativada" },
    { id: 30, value: 30, label: "30 s" },
    { id: 60, value: 60, label: "1 min" },
    { id: 300, value: 300, label: "5 min" },
];
const GRUPO_TODOS = { id: "", value: "", label: "Todos" };
const GRUPO_OPTIONS = [GRUPO_TODOS, ...Object.entries(GRUPOS).map(([value, g]) => ({ id: value, value, label: g.rotulo }))];
const rotulo = { px: 1, pb: 0.5, pt: 0.2 };
const celula = { verticalAlign: "top", pt: 1.5 };
const POR_PAGINA = 20;
// Reenviar os e-mails dos robôs: Monitor de Robôs com a opção Editar (o backend confere de novo)
const PERMISSAO_REENVIAR = "c083_U";
const iconeLinha = { fontSize: "0.9rem !important", verticalAlign: "middle", mr: 0.5 };

/** Cor de um tom do tema (success, error...) no modo claro e no escuro; sem tom = cinza do texto fraco. */
function useCorDoTom() {
    const theme = useTheme();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    return useCallback((tom) => {
        if (!tom) return cores.textoFraco;
        return darkMode ? TEXTO_COLORIDO_ESCURO[tom] || cores.texto : theme.palette[tom]?.main || cores.texto;
    }, [darkMode, theme, cores]);
}

/** Selo de situação: ícone colorido + rótulo (a cor nunca vai sozinha). */
function Selo({ icone, tom, rotulo: texto, girar }) {
    const corDoTom = useCorDoTom();
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const cor = corDoTom(tom);
    return (
        <MDBox component="span" display="inline-flex" alignItems="center" gap={0.5}
            sx={{ px: 1, py: 0.25, borderRadius: "12px", border: `1px solid ${cor}`, whiteSpace: "nowrap" }}>
            <Icon sx={{
                color: cor, fontSize: "1rem !important",
                ...(girar ? { animation: "girarMonitor 1.6s linear infinite", "@keyframes girarMonitor": { to: { transform: "rotate(-360deg)" } } } : {}),
            }}>{icone}</Icon>
            <MDTypography component="span" variant="caption" fontWeight="bold" sx={{ color: cores.texto }}>{texto}</MDTypography>
        </MDBox>
    );
}

function Indicador({ grupo, valor, selecionado, onClick }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const corDoTom = useCorDoTom();
    const g = GRUPOS[grupo];
    const cor = corDoTom(g.tom);
    return (
        <Card onClick={onClick} role="button" tabIndex={0} aria-pressed={selecionado}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
            sx={{
                p: 2, height: "100%", cursor: "pointer", background: cores.cartao,
                boxShadow: selecionado ? `inset 0 0 0 2px ${cor}` : undefined,
                "&:hover": selecionado ? undefined : { boxShadow: `inset 0 0 0 1px ${cor}` },
            }}>
            <MDBox display="flex" alignItems="center" gap={2}>
                <Icon sx={{ color: cor, fontSize: "2rem !important" }}>{g.icone}</Icon>
                <MDBox minWidth={0}>
                    <MDTypography variant="button" color="text" sx={{ display: "block", fontWeight: 500 }}>{g.rotulo}</MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="text" sx={{ lineHeight: 1.2 }}>{valor}</MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>{g.detalhe}</MDTypography>
                </MDBox>
            </MDBox>
        </Card>
    );
}

function Instancias({ instancias, agora }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    if (!instancias.length) {
        return (
            <Card sx={{ p: 2, background: cores.cartao }}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Icon sx={{ color: cores.textoFraco }}>dns</Icon>
                    <MDTypography variant="button" color="text">
                        Nenhum serviço de robôs registrado ainda. O serviço se registra ao subir (a partir da versão com o monitor)
                        e manda sinal de vida a cada minuto.
                    </MDTypography>
                </MDBox>
            </Card>
        );
    }
    return (
        <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
            <Grid container spacing={2}>
                {instancias.map((i) => (
                    <Grid item xs={12} md={6} lg={4} key={i.maquina}>
                        <MDBox display="flex" alignItems="flex-start" gap={1.5}>
                            <Icon sx={{ mt: 0.25, color: cores.textoSecundario }}>dns</Icon>
                            <MDBox minWidth={0}>
                                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <MDTypography variant="button" fontWeight="bold" color="text">{i.maquina}</MDTypography>
                                    {i.ativa
                                        ? <Selo icone="favorite" tom="success" rotulo="No ar" />
                                        : <Selo icone="power_off" tom="error" rotulo="Sem sinal de vida" />}
                                </MDBox>
                                <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                                    Perfil {i.perfil || "-"} · {i.qtdRobos} robô{i.qtdRobos === 1 ? "" : "s"} · último sinal {tempoRelativo(i.ultimoSinal, agora)}
                                    {i.inicio ? ` · no ar desde ${dataCurta(i.inicio, agora)}` : ""}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </Grid>
                ))}
            </Grid>
        </Card>
    );
}

function CartaoRobo({ robo, agora, onAbrir }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const corDoTom = useCorDoTom();
    const f = FAROIS[robo.farol] || FAROIS.AGUARDANDO;
    const cor = corDoTom(f.tom);
    const comErro = robo.farol === "ERRO" && robo.ultimoErroTexto;
    return (
        <Card onClick={onAbrir} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") onAbrir(); }}
            sx={{
                p: 2, height: "100%", cursor: "pointer", background: cores.cartao, borderLeft: `4px solid ${cor}`,
                "&:hover": { background: cores.hover },
            }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                <MDBox minWidth={0}>
                    <MDTypography variant="button" fontWeight="bold" color="text" sx={{ display: "block", lineHeight: 1.3 }}>{robo.nome}</MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", color: cores.textoFraco }}>{robo.agendaDescricao}</MDTypography>
                </MDBox>
                <Tooltip title={robo.farolDescricao || ""} placement="top">
                    <span><Selo icone={f.icone} tom={f.tom} rotulo={robo.farolLabel} girar={robo.farol === "EM_EXECUCAO"} /></span>
                </Tooltip>
            </MDBox>
            <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                {robo.ultimoInicio
                    ? <>Última: <b>{tempoRelativo(robo.ultimoInicio, agora)}</b>
                        {robo.farol !== "EM_EXECUCAO" && robo.ultimaDuracao !== null && robo.ultimaDuracao !== undefined ? ` · durou ${formatarDuracao(robo.ultimaDuracao)}` : ""}
                        {robo.ultimaSituacaoLabel ? ` · ${robo.ultimaSituacaoLabel}` : ""}</>
                    : "Ainda não rodou"}
            </MDTypography>
            {robo.ultimoResumo && (
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>{robo.ultimoResumo}</MDTypography>
            )}
            {robo.aviso && (
                <MDTypography variant="caption" sx={{ display: "block", mt: 0.25, color: corDoTom("info"), fontWeight: "bold" }}>{robo.aviso}</MDTypography>
            )}
            {comErro && (
                <MDTypography variant="caption" sx={{ display: "block", mt: 0.25, color: corDoTom("error"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {robo.ultimoErroTexto.split("\n")[0]}
                </MDTypography>
            )}
            <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} mt={1}>
                <MDTypography variant="caption" color="text" sx={{ color: cores.textoFraco }}>
                    {/* Serviço parado: a próxima prevista já passou, só roda quando o serviço voltar */}
                    {robo.farol === "PARADO" ? "Próxima: quando o serviço voltar"
                        : robo.proximaExecucao ? `Próxima: ${dataCurta(robo.proximaExecucao, agora)}` : ""}
                </MDTypography>
                <MDTypography variant="caption" color="text" sx={{ color: cores.textoFraco }}>
                    24h: {robo.erros24h > 0 ? <b style={{ color: corDoTom("error") }}>{robo.erros24h} com erro</b> : "sem erro"}
                    {robo.processados24h > 0 ? ` · ${robo.processados24h} processado${robo.processados24h === 1 ? "" : "s"}` : ""}
                </MDTypography>
            </MDBox>
        </Card>
    );
}

function Campo({ titulo, children }) {
    return (
        <MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>{titulo}</MDTypography>
            <MDTypography variant="caption" color="text" component="div">{children}</MDTypography>
        </MDBox>
    );
}

function TextoErro({ texto }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    return (
        <MDBox component="pre" sx={{
            m: 0, p: 1.5, maxHeight: 320, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
            fontSize: "0.72rem", lineHeight: 1.45, background: cores.painelEscuro, color: cores.texto,
            border: `1px solid ${cores.borda}`, borderRadius: "6px",
        }}>{texto}</MDBox>
    );
}

const emailGuardado = (email) => email.tamanho !== null && email.tamanho !== undefined;

// Arquivos que o robô guarda com a execução (tipo do ral-robot): rótulo e ícone. CHROME, PAGINA e ALERTA são do
// Chrome do próprio robô no erro (ChromeDoRobo); TELA é a tela do Windows da máquina
const ARQUIVOS_EXECUCAO = {
    ENVIADO: { rotulo: "Enviado", icone: "outbox" },
    RETORNO: { rotulo: "Retorno", icone: "move_to_inbox" },
    TELA: { rotulo: "Tela no erro", icone: "photo_camera" },
    CHROME: { rotulo: "Chrome no erro", icone: "web" },
    PAGINA: { rotulo: "Página no erro (HTML)", icone: "code" },
    ALERTA: { rotulo: "Alerta aberto no erro", icone: "warning" },
};

/** Um e-mail que o robô mandou na execução: assunto, para quem, se saiu e os reenvios já feitos. */
function EmailRobo({ email, selecionado, podeReenviar, onSelecionar, onVer, onReenviar }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const corDoTom = useCorDoTom();
    const guardado = emailGuardado(email);
    const situacaoConhecida = email.enviado !== null && email.enviado !== undefined;
    return (
        <MDBox display="flex" alignItems="flex-start" gap={1} py={1} sx={{ borderTop: `1px solid ${cores.divisoria}` }}>
            {podeReenviar && (
                <Checkbox size="small" checked={selecionado} disabled={!guardado} onChange={(e) => onSelecionar(e.target.checked)}
                    inputProps={{ "aria-label": `Escolher o e-mail ${email.assunto || ""}` }} sx={{ p: 0.25, mt: 0.25 }} />
            )}
            <MDBox minWidth={0} flex={1}>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ wordBreak: "break-word" }}>
                        {email.assunto || "(sem assunto)"}
                    </MDTypography>
                    {situacaoConhecida && (
                        <Selo icone={email.enviado ? "mark_email_read" : "error"} tom={email.enviado ? "success" : "error"}
                            rotulo={email.enviado ? "Enviado" : "Não enviado"} />
                    )}
                </MDBox>
                <MDTypography variant="caption" color="text" component="div" sx={{ wordBreak: "break-word" }}>
                    Para: {email.para || "-"}{email.anexo ? ` · Anexo: ${email.anexo}` : ""}
                </MDTypography>
                {email.entreguePara && (
                    <MDTypography variant="caption" component="div" sx={{ color: cores.textoFraco, wordBreak: "break-word" }}>
                        Foi de fato para {email.entreguePara} (robô fora de produção ou com o e-mail de teste ligado)
                    </MDTypography>
                )}
                {email.anexoFora && (
                    <MDTypography variant="caption" component="div" sx={{ color: cores.textoFraco }}>
                        Anexo grande demais para guardar: {email.anexoFora}. O reenvio sai sem ele.
                    </MDTypography>
                )}
                {email.erro && (
                    <MDTypography variant="caption" component="div" sx={{ color: corDoTom("error"), wordBreak: "break-word" }}>{email.erro}</MDTypography>
                )}
                {(email.reenvios || []).map((r, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <MDTypography key={i} variant="caption" component="div"
                        sx={{ color: r.enviado ? cores.textoSecundario : corDoTom("error"), wordBreak: "break-word" }}>
                        <Icon sx={iconeLinha}>{r.enviado ? "forward_to_inbox" : "error"}</Icon>
                        {r.enviado ? "Reenviado" : "Reenvio falhou"} por {r.usuario} em {dataHora(r.data)} para {r.para || "-"}{r.erro ? `: ${r.erro}` : ""}
                    </MDTypography>
                ))}
            </MDBox>
            {guardado && (
                <MDBox display="flex" flexShrink={0}>
                    <MDButton variant="text" color="info" size="small" onClick={onVer}><Icon sx={{ mr: 0.5 }}>visibility</Icon>Ver</MDButton>
                    {podeReenviar && (
                        <MDButton variant="text" color="info" size="small" onClick={onReenviar}>
                            <Icon sx={{ mr: 0.5 }}>forward_to_inbox</Icon>Reenviar
                        </MDButton>
                    )}
                </MDBox>
            )}
        </MDBox>
    );
}

/**
 * Reenvio de e-mails dos robôs: o mesmo e-mail (assunto, texto e anexos), pela caixa do sistema. Um só já vem com os
 * destinatários originais, que dá para trocar; vários, em branco = os originais de cada um.
 */
function ReenvioEmails({ emails, onFechar, onReenviado, url = "/robo-monitor/emails/reenviar" }) {
    const um = emails.length === 1;
    const corDoTom = useCorDoTom();
    const [para, setPara] = useState(um ? emails[0].para || "" : "");
    const [enviando, setEnviando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [erro, setErro] = useState("");
    const reenviar = () => {
        setEnviando(true);
        setErro("");
        post(url, { ids: emails.map((e) => e.id), para: para.trim() || null })
            .then((r) => { setResultado(r); onReenviado(); })
            .catch((e) => setErro(e?.message || "Não foi possível reenviar."))
            .finally(() => setEnviando(false));
    };
    return (
        <Dialog open onClose={enviando ? undefined : onFechar} maxWidth="sm" fullWidth>
            <DialogTitle>Reenviar {um ? "e-mail" : `${emails.length} e-mails`}</DialogTitle>
            <DialogContent dividers>
                {!resultado ? (
                    <>
                        <MDTypography variant="caption" color="text" component="div" sx={{ mb: 1.5, wordBreak: "break-word" }}>
                            {um ? <>Assunto: <b>{emails[0].assunto || "(sem assunto)"}</b>. </> : `${emails.length} e-mails escolhidos. `}
                            Sai o mesmo e-mail (assunto, texto e anexos), pela caixa do sistema.
                        </MDTypography>
                        <InputLabel sx={rotulo}>Para</InputLabel>
                        <MDInput size="small" fullWidth multiline minRows={2} value={para} onChange={(e) => setPara(e.target.value)}
                            placeholder={um ? "" : "Em branco = os destinatários originais de cada e-mail"} />
                        <MDTypography variant="caption" color="text" component="div" sx={{ mt: 0.5 }}>
                            {um ? "Já vem com os destinatários originais: troque ou acrescente, separando com ponto e vírgula."
                                : "Preenchido, todos os e-mails escolhidos vão para esses endereços (separe com ponto e vírgula)."}
                        </MDTypography>
                        {erro && <MDTypography variant="caption" component="div" sx={{ mt: 1, color: corDoTom("error") }}>{erro}</MDTypography>}
                    </>
                ) : (
                    <>
                        <MDTypography variant="button" color="text" component="div" sx={{ mb: 1 }}>
                            {resultado.enviados} reenviado{resultado.enviados === 1 ? "" : "s"}
                            {resultado.comErro ? `, ${resultado.comErro} com erro` : ""}.
                            {resultado.desviadoPara ? ` Fora de produção: foi para ${resultado.desviadoPara}.` : ""}
                        </MDTypography>
                        {(resultado.itens || []).map((i) => (
                            <MDTypography key={i.id} variant="caption" color="text" component="div"
                                sx={{ wordBreak: "break-word", ...(i.enviado ? {} : { color: corDoTom("error") }) }}>
                                <Icon sx={iconeLinha}>{i.enviado ? "check_circle" : "error"}</Icon>
                                {i.assunto || "(sem assunto)"}: {i.enviado ? `para ${i.para}` : i.erro}
                            </MDTypography>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {resultado ? (
                    <MDButton variant="text" color="info" onClick={onFechar}>Fechar</MDButton>
                ) : (
                    <>
                        <MDButton variant="text" color="info" onClick={onFechar} disabled={enviando}>Cancelar</MDButton>
                        <MDButton variant="contained" color="info" onClick={reenviar} disabled={enviando || (um && !para.trim())}>
                            {enviando ? "Reenviando..." : "Reenviar"}
                        </MDButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

/** Uma execução: erro completo, arquivos (o CSV enviado e o retorno do WIBS abrem no visualizador) e os e-mails que o robô mandou. */
function DetalheExecucao({ id, onFechar, onErro }) {
    const { abrirArquivos } = useVisualizadorArquivos();
    const { userFunctionalities } = useUser();
    const podeReenviar = Boolean(userFunctionalities && userFunctionalities.includes(PERMISSAO_REENVIAR));
    const [execucao, setExecucao] = useState(null);
    const [selecionados, setSelecionados] = useState([]);
    const [reenvio, setReenvio] = useState(null);
    const carregar = useCallback(() => get(`/robo-monitor/execucoes/${encodeURIComponent(id)}`).then(setExecucao), [id]);
    useEffect(() => {
        if (!id) return;
        setExecucao(null);
        setSelecionados([]);
        carregar().catch((e) => { onErro(e?.message || "Não foi possível abrir a execução."); onFechar(); });
    }, [id, carregar, onErro, onFechar]);
    const emails = execucao?.emails || [];
    const emailsGuardados = emails.filter(emailGuardado);
    const urlDoEmail = (email) => `/robo-monitor/arquivos/${encodeURIComponent(email.id)}`;
    // O visualizador abre o .eml (cabeçalho, corpo e anexos) e navega entre os e-mails da execução
    const emailsVisualizaveis = emailsGuardados.map((e) => ({ url: urlDoEmail(e), nome: e.nome, tipo: "E-mail" }));
    const todosMarcados = emailsGuardados.length > 0 && selecionados.length === emailsGuardados.length;
    const s = execucao ? SITUACOES[execucao.situacao] || {} : {};
    const arquivos = (execucao?.arquivos || []).map((a) => ({
        ...a,
        guardado: a.tamanho !== null && a.tamanho !== undefined,
        url: `/robo-monitor/arquivos/${encodeURIComponent(a.id)}`,
        rotulo: ARQUIVOS_EXECUCAO[a.tipo]?.rotulo || a.tipo,
        icone: ARQUIVOS_EXECUCAO[a.tipo]?.icone || "outbox",
    }));
    // O visualizador navega entre os arquivos da execução (ex.: do CSV enviado para o retorno do WIBS)
    const visualizaveis = arquivos.filter((a) => a.guardado).map((a) => ({ url: a.url, nome: a.nome, tipo: a.rotulo }));
    return (
        <Dialog open={!!id} onClose={onFechar} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                Execução {execucao ? `de ${dataHora(execucao.inicio)}` : ""}
                {execucao && <Selo icone={s.icone || "help"} tom={s.tom} rotulo={execucao.situacaoLabel || "-"} />}
            </DialogTitle>
            <DialogContent dividers>
                {!execucao ? (
                    <MDBox display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></MDBox>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}><Campo titulo="Robô">{execucao.nome}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Máquina">{execucao.maquina || "-"}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Fim">{execucao.fim ? dataHora(execucao.fim) : "-"}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Duração">{formatarDuracao(execucao.duracao)}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Processados">{execucao.processados ?? "-"}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Erros no log">{execucao.erros ?? "-"}</Campo></Grid>
                        <Grid item xs={12} sm={6}><Campo titulo="Resumo">{execucao.resumo || "-"}</Campo></Grid>
                        {emails.length > 0 && (
                            <Grid item xs={12}>
                                <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={0.5}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text">E-mails que o robô mandou ({emails.length})</MDTypography>
                                    {podeReenviar && emailsGuardados.length > 0 && (
                                        <MDBox display="flex" gap={1} flexWrap="wrap">
                                            <MDButton variant="text" color="info" size="small"
                                                onClick={() => setSelecionados(todosMarcados ? [] : emailsGuardados.map((e) => e.id))}>
                                                {todosMarcados ? "Desmarcar todos" : "Marcar todos"}
                                            </MDButton>
                                            <MDButton variant="outlined" color="info" size="small" disabled={selecionados.length === 0}
                                                onClick={() => setReenvio(emails.filter((e) => selecionados.includes(e.id)))}>
                                                <Icon sx={{ mr: 0.5 }}>forward_to_inbox</Icon>Reenviar escolhidos ({selecionados.length})
                                            </MDButton>
                                        </MDBox>
                                    )}
                                </MDBox>
                                {emails.map((e) => (
                                    <EmailRobo key={e.id} email={e} podeReenviar={podeReenviar} selecionado={selecionados.includes(e.id)}
                                        onSelecionar={(marcar) => setSelecionados((s) => (marcar ? [...s, e.id] : s.filter((x) => x !== e.id)))}
                                        onVer={() => abrirArquivos(emailsVisualizaveis, emailsVisualizaveis.findIndex((v) => v.url === urlDoEmail(e)))}
                                        onReenviar={() => setReenvio([e])} />
                                ))}
                                {!podeReenviar && (
                                    <MDTypography variant="caption" color="text" component="div" sx={{ mt: 0.5 }}>
                                        Para reenviar, o perfil precisa do Monitor de Robôs com a opção Editar.
                                    </MDTypography>
                                )}
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>Arquivos</MDTypography>
                            {arquivos.length === 0 ? (
                                <MDTypography variant="caption" color="text">Nenhum arquivo guardado nesta execução.</MDTypography>
                            ) : arquivos.map((a) => (
                                <MDBox key={a.id || a.nome} display="flex" alignItems="center" gap={1} flexWrap="wrap" py={0.5}>
                                    <Icon fontSize="small">{a.icone}</Icon>
                                    <MDTypography variant="caption" color="text" sx={{ minWidth: 0, wordBreak: "break-all" }}>
                                        <b>{a.rotulo}</b>: {a.nome} ({formatarTamanho(a.tamanho)})
                                    </MDTypography>
                                    {a.guardado ? (
                                        <>
                                            <MDButton variant="text" color="info" size="small"
                                                onClick={() => abrirArquivos(visualizaveis, visualizaveis.findIndex((v) => v.url === a.url))}>
                                                <Icon sx={{ mr: 0.5 }}>visibility</Icon>Ver
                                            </MDButton>
                                            <MDButton variant="text" color="info" size="small" onClick={() => getFile(a.url, a.nome).catch((e) => onErro(e?.message))}>
                                                <Icon sx={{ mr: 0.5 }}>download</Icon>Baixar
                                            </MDButton>
                                        </>
                                    ) : (
                                        <MDTypography variant="caption" color="text">(maior que 10 MB: só o nome foi registrado)</MDTypography>
                                    )}
                                </MDBox>
                            ))}
                        </Grid>
                        {execucao.erro && (
                            <Grid item xs={12}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>Erros</MDTypography>
                                <TextoErro texto={execucao.erro} />
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="info" onClick={onFechar}>Fechar</MDButton>
            </DialogActions>
            {reenvio && (
                <ReenvioEmails emails={reenvio} onFechar={() => setReenvio(null)}
                    onReenviado={() => { setSelecionados([]); carregar().catch(() => {}); }} />
            )}
        </Dialog>
    );
}

/**
 * Itens Enviados da caixa do sistema (Microsoft 365): conferir se um e-mail dos robôs ou do sistema saiu, para quem e
 * quando, abrir e reenviar, sem precisar abrir a caixa no Outlook. Os de senha e 2FA não aparecem (backend).
 */
function EnviadosCaixaSistema({ aberto, onFechar }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const corDoTom = useCorDoTom();
    const { abrirArquivos } = useVisualizadorArquivos();
    const hoje = dataIso(new Date());
    const [de, setDe] = useState(hoje);
    const [ate, setAte] = useState(hoje);
    const [busca, setBusca] = useState("");
    const [resultado, setResultado] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState("");
    const [selecionados, setSelecionados] = useState([]);
    const [reenvio, setReenvio] = useState(null);

    const buscar = useCallback(() => {
        setCarregando(true);
        setErro("");
        setSelecionados([]);
        const params = new URLSearchParams({ de: de || "", ate: ate || de || "", busca: busca.trim() });
        get(`/robo-monitor/caixa-sistema/enviados?${params.toString()}`)
            .then(setResultado)
            .catch((e) => { setResultado(null); setErro(e?.message || "Não foi possível ler a caixa do sistema."); })
            .finally(() => setCarregando(false));
    }, [de, ate, busca]);

    useEffect(() => {
        if (aberto) buscar();
        // Só ao abrir: depois, a busca é pelo botão
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    const itens = resultado?.itens || [];
    const urlDoEml = (e) => `/robo-monitor/caixa-sistema/eml?id=${encodeURIComponent(e.id)}`;
    const nomeDoEml = (e) => `${(e.assunto || "e-mail").replace(/[\\/:*?"<>|]+/g, " ").slice(0, 120)}.eml`;
    const todosMarcados = itens.length > 0 && selecionados.length === itens.length;
    const marcar = (id, sim) => setSelecionados((s) => (sim ? [...s, id] : s.filter((x) => x !== id)));

    return (
        <Dialog open={aberto} onClose={onFechar} maxWidth="lg" fullWidth>
            <DialogTitle>E-mails enviados pela caixa do sistema</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={6} md={4}>
                        <MDPeriodo inicio={de} fim={ate} onChange={({ inicio, fim }) => { setDe(inicio); setAte(fim); }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={5}>
                        <InputLabel sx={rotulo}>Buscar</InputLabel>
                        <MDInput size="small" fullWidth value={busca} onChange={(e) => setBusca(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") buscar(); }} placeholder="Ex.: EPROC, Cível 07, nº do processo, e-mail da equipe" />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <MDButton variant="contained" color="info" size="medium" fullWidth onClick={buscar} disabled={carregando}>
                            <Icon sx={{ mr: 0.5 }}>search</Icon>Buscar
                        </MDButton>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: "8px !important" }}>
                        <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                            Itens Enviados da caixa {resultado?.caixa || "do sistema"} (Microsoft 365), período de até 31 dias. A busca procura no
                            assunto, nos destinatários e no texto do e-mail (sem ela, a caixa manda centenas por dia: use um período curto).
                            Reenviar manda o mesmo e-mail (assunto, texto e anexos) de novo pela caixa do sistema. E-mails de senha e de 2FA não aparecem.
                        </MDTypography>
                    </Grid>
                </Grid>
                <MDBox height={4} my={1}>{carregando && <LinearProgress color="info" />}</MDBox>
                {erro && <MDTypography variant="caption" component="div" sx={{ color: corDoTom("error"), mb: 1 }}>{erro}</MDTypography>}
                {resultado && (
                    <>
                        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1}>
                            <MDTypography variant="caption" color="text">
                                {itens.length} e-mail{itens.length === 1 ? "" : "s"}
                                {busca.trim() ? ` com "${busca.trim()}"` : ""} de {resultado.lidos} enviado{resultado.lidos === 1 ? "" : "s"} no período
                                {resultado.ocultos ? ` (${resultado.ocultos} de senha/2FA ocultos)` : ""}.
                                {resultado.limiteAtingido ? (busca.trim()
                                    ? " A busca do Microsoft 365 parou nos mais recentes (o termo é comum demais): use um termo mais específico ou um período menor."
                                    : " O período tem mais e-mails do que a consulta lê: diminua o período ou use a busca.") : ""}
                            </MDTypography>
                            {itens.length > 0 && (
                                <MDBox display="flex" gap={1} flexWrap="wrap">
                                    <MDButton variant="text" color="info" size="small" onClick={() => setSelecionados(todosMarcados ? [] : itens.map((e) => e.id))}>
                                        {todosMarcados ? "Desmarcar todos" : "Marcar todos"}
                                    </MDButton>
                                    <MDButton variant="outlined" color="info" size="small" disabled={selecionados.length === 0}
                                        onClick={() => setReenvio(itens.filter((e) => selecionados.includes(e.id)))}>
                                        <Icon sx={{ mr: 0.5 }}>forward_to_inbox</Icon>Reenviar escolhidos ({selecionados.length})
                                    </MDButton>
                                </MDBox>
                            )}
                        </MDBox>
                        {itens.length === 0 ? (
                            <MDTypography variant="caption" color="text">Nenhum e-mail enviado com esses filtros.</MDTypography>
                        ) : (
                            <TableContainer sx={{ boxShadow: "none", borderRadius: 0, background: "transparent", maxHeight: 460 }}>
                                <Table sx={{ minWidth: 760 }}>
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            <DataTableHeadCell width="40px" sorted={false} />
                                            <DataTableHeadCell width="130px" sorted={false}>Enviado em</DataTableHeadCell>
                                            <DataTableHeadCell sorted={false}>Assunto</DataTableHeadCell>
                                            <DataTableHeadCell sorted={false}>Para</DataTableHeadCell>
                                            <DataTableHeadCell width="170px" sorted={false} align="right">Ações</DataTableHeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {itens.map((e) => (
                                            <TableRow key={e.id} hover>
                                                <TableCell sx={celula}>
                                                    <Checkbox size="small" checked={selecionados.includes(e.id)} onChange={(ev) => marcar(e.id, ev.target.checked)}
                                                        inputProps={{ "aria-label": `Escolher o e-mail ${e.assunto || ""}` }} sx={{ p: 0.25 }} />
                                                </TableCell>
                                                <TableCell sx={celula}><MDTypography variant="caption" color="text">{dataHora(e.data)}</MDTypography></TableCell>
                                                <TableCell sx={celula}>
                                                    <MDTypography variant="caption" color="text" sx={{ wordBreak: "break-word" }}>
                                                        {e.anexos && <Icon sx={iconeLinha} titleAccess="Com anexo">attach_file</Icon>}
                                                        {e.assunto || "(sem assunto)"}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell sx={celula}>
                                                    <MDTypography variant="caption" color="text" component="div" sx={{ wordBreak: "break-word" }}>{e.para || "-"}</MDTypography>
                                                    {e.cc && <MDTypography variant="caption" component="div" sx={{ color: cores.textoFraco, wordBreak: "break-word" }}>Cc: {e.cc}</MDTypography>}
                                                </TableCell>
                                                <TableCell sx={{ ...celula, whiteSpace: "nowrap" }} align="right">
                                                    <MDButton variant="text" color="info" size="small"
                                                        onClick={() => abrirArquivos([{ url: urlDoEml(e), nome: nomeDoEml(e), tipo: "E-mail" }], 0)}>
                                                        <Icon sx={{ mr: 0.5 }}>visibility</Icon>Ver
                                                    </MDButton>
                                                    <MDButton variant="text" color="info" size="small" onClick={() => setReenvio([e])}>
                                                        <Icon sx={{ mr: 0.5 }}>forward_to_inbox</Icon>Reenviar
                                                    </MDButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="info" onClick={onFechar}>Fechar</MDButton>
            </DialogActions>
            {reenvio && (
                <ReenvioEmails emails={reenvio} url="/robo-monitor/caixa-sistema/reenviar" onFechar={() => setReenvio(null)}
                    onReenviado={() => setSelecionados([])} />
            )}
        </Dialog>
    );
}

const INTERVALO_TELA_MS = 4000;
// Foto mais velha que isso não é "ao vivo": o robô terminou (o painel ainda não atualizou) ou parou de fotografar
const FOTO_AO_VIVO_MS = 15000;

const horaComSegundos = (data) => new Date(data).toLocaleTimeString("pt-BR");

/**
 * Tela da máquina do robô: enquanto ele trabalha, a última foto da tela, atualizada a cada 4 s (o ral-robot fotografa
 * a tela do Windows, TelaDaMaquina). Fora da execução, só a referência da última foto, que abre ao clicar.
 */
function TelaAgora({ robo }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const [tela, setTela] = useState(null);
    const [carregou, setCarregou] = useState(false);
    const [verUltima, setVerUltima] = useState(false);
    const [ampliada, setAmpliada] = useState(false);
    const trabalhando = robo?.farol === "EM_EXECUCAO";
    const maquina = robo?.maquina;

    useEffect(() => {
        if (!maquina) return undefined;
        let ativo = true;
        const carregar = () => get(`/robo-monitor/tela/${encodeURIComponent(maquina)}`)
            .then((t) => { if (ativo) { setTela(t || null); setCarregou(true); } })
            .catch(() => { if (ativo) setCarregou(true); });
        carregar();
        const timer = trabalhando ? setInterval(carregar, INTERVALO_TELA_MS) : null;
        return () => { ativo = false; if (timer) clearInterval(timer); };
    }, [maquina, trabalhando]);

    // Clicar alterna entre caber na janela e o tamanho real (com dois monitores, a foto fica larga e pequena)
    const imagem = tela?.imagem ? (
        <MDBox sx={{ mt: 1, overflow: "auto", maxHeight: ampliada ? 600 : "none", border: `1px solid ${cores.borda}`, borderRadius: "6px" }}>
            <MDBox component="img" src={`data:image/jpeg;base64,${tela.imagem}`} alt={`Tela da máquina ${maquina}`}
                title={ampliada ? "Clique para caber na janela" : "Clique para ver no tamanho real"}
                onClick={() => setAmpliada((a) => !a)}
                sx={{ display: "block", width: ampliada ? "auto" : "100%", maxWidth: ampliada ? "none" : "100%",
                    cursor: ampliada ? "zoom-out" : "zoom-in" }} />
        </MDBox>
    ) : null;
    const aoVivo = trabalhando && tela?.data && Date.now() - tela.data < FOTO_AO_VIVO_MS;

    return (
        <MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>
                Tela da máquina {maquina || ""}
            </MDTypography>
            {!carregou ? (
                <CircularProgress color="info" size={18} />
            ) : trabalhando ? (
                tela?.aviso ? (
                    <MDTypography variant="caption" color="warning" sx={{ display: "block" }}>{tela.aviso}</MDTypography>
                ) : tela?.imagem ? (
                    <>
                        <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                            {aoVivo ? "Ao vivo, atualiza a cada 4 s · foto" : "Última foto (o robô terminou ou parou de enviar fotos)"} das {horaComSegundos(tela.data)}
                            {tela.robos ? ` · trabalhando: ${tela.robos}` : ""}
                        </MDTypography>
                        {imagem}
                    </>
                ) : (
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        Aguardando a primeira foto (o robô fotografa a tela a cada 4 s enquanto trabalha).
                    </MDTypography>
                )
            ) : (
                <>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        A tela aparece aqui ao vivo enquanto o robô trabalha. Quando uma execução dá erro, a última foto fica nos arquivos dela.
                        {tela?.data ? ` Última foto da máquina: ${dataHora(tela.data)}${tela.robos ? ` (trabalhando: ${tela.robos})` : ""}.` : ""}
                    </MDTypography>
                    {tela?.imagem && (
                        <MDButton variant="text" color="info" size="small" onClick={() => setVerUltima((v) => !v)}>
                            <Icon sx={{ mr: 0.5 }}>{verUltima ? "visibility_off" : "visibility"}</Icon>
                            {verUltima ? "Esconder a última foto" : "Ver a última foto"}
                        </MDButton>
                    )}
                    {verUltima && imagem}
                </>
            )}
        </MDBox>
    );
}

/** Detalhe de um robô: situação atual e histórico de execuções com filtros. */
function DetalheRobo({ robo, agora, onFechar, onErro }) {
    const [verErroAntigo, setVerErroAntigo] = useState(false);
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const hoje = new Date();
    const [de, setDe] = useState(dataIso(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 7)));
    const [ate, setAte] = useState(dataIso(hoje));
    const [situacao, setSituacao] = useState(FILTROS_SITUACAO[0]);
    const [pagina, setPagina] = useState(0);
    const [resultado, setResultado] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [execucaoAberta, setExecucaoAberta] = useState(null);

    const buscar = useCallback((numeroPagina) => {
        if (!robo) return;
        setCarregando(true);
        post("/robo-monitor/execucoes", { robo: robo.chave, de, ate, situacoes: situacao.situacoes, page: numeroPagina, pageSize: POR_PAGINA })
            .then((r) => { setResultado(r); setPagina(numeroPagina); })
            .catch((e) => onErro(e?.message || "Não foi possível carregar o histórico."))
            .finally(() => setCarregando(false));
    }, [robo, de, ate, situacao, onErro]);

    useEffect(() => {
        setResultado(null);
        if (robo) buscar(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [robo?.chave]);

    const fecharExecucao = useCallback(() => setExecucaoAberta(null), []);
    if (!robo) return null;
    const f = FAROIS[robo.farol] || FAROIS.AGUARDANDO;
    // Erro antigo: o robô já rodou com sucesso depois dele (o farol vem da última execução, não do último erro)
    const erroResolvido = Boolean(robo.ultimoErro && robo.ultimoSucesso && new Date(robo.ultimoSucesso) > new Date(robo.ultimoErro));
    const totalPaginas = resultado ? Math.max(1, Math.ceil(resultado.total / POR_PAGINA)) : 1;
    const bordaLinha = { borderColor: cores.divisoria };

    return (
        <Dialog open={!!robo} onClose={onFechar} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {robo.nome}
                <Selo icone={f.icone} tom={f.tom} rotulo={robo.farolLabel} girar={robo.farol === "EM_EXECUCAO"} />
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    {robo.farolDescricao && (
                        <Grid item xs={12}>
                            <MDTypography variant="caption" color="text">{robo.farolDescricao}</MDTypography>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Agenda">{robo.agendaDescricao}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Máquina">{robo.maquina || "-"}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Último início">{robo.ultimoInicio ? `${dataHora(robo.ultimoInicio)} (${tempoRelativo(robo.ultimoInicio, agora)})` : "-"}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Próxima prevista">{robo.farol === "PARADO" ? "Quando o serviço voltar" : robo.proximaExecucao ? dataHora(robo.proximaExecucao) : "-"}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Último sucesso">{robo.ultimoSucesso ? dataHora(robo.ultimoSucesso) : "-"}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Campo titulo="Último erro">
                            {robo.ultimoErro ? dataHora(robo.ultimoErro) : "-"}
                            {erroResolvido && <span style={{ color: cores.textoFraco }}> (resolvido: houve sucesso depois)</span>}
                        </Campo>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}><Campo titulo="Identificação no código">{robo.chave}</Campo></Grid>
                    {robo.ultimoErroTexto && (
                        <Grid item xs={12}>
                            {erroResolvido ? (
                                <>
                                    <MDTypography variant="caption" color="text" component="div">
                                        O último erro ({dataHora(robo.ultimoErro)}) já foi superado: o robô rodou com sucesso depois, em{" "}
                                        {dataHora(robo.ultimoSucesso)}.{" "}
                                        <MDButton variant="text" color="info" size="small" sx={{ p: 0, minHeight: 0 }} onClick={() => setVerErroAntigo(!verErroAntigo)}>
                                            {verErroAntigo ? "esconder o erro" : "ver o erro"}
                                        </MDButton>
                                    </MDTypography>
                                    {verErroAntigo && <MDBox mt={0.5}><TextoErro texto={robo.ultimoErroTexto} /></MDBox>}
                                </>
                            ) : (
                                <>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>Último erro registrado</MDTypography>
                                    <TextoErro texto={robo.ultimoErroTexto} />
                                </>
                            )}
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TelaAgora robo={robo} />
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={{ px: 2, py: 1.5, background: cores.painel, boxShadow: "none", border: `1px solid ${cores.divisoria}` }}>
                            <Grid container spacing={2} alignItems="flex-end">
                                <Grid item xs={12} sm={6} md={4}>
                                    <MDPeriodo inicio={de} fim={ate} onChange={({ inicio, fim }) => { setDe(inicio); setAte(fim); }} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <InputLabel sx={rotulo}>Situação</InputLabel>
                                    <MDSelect name="situacao" value={situacao} options={FILTROS_SITUACAO} onChange={(v) => setSituacao(v || FILTROS_SITUACAO[0])} />
                                </Grid>
                                <Grid item xs={12} sm={12} md={4}>
                                    <MDButton variant="contained" color="info" size="medium" fullWidth onClick={() => buscar(0)} disabled={carregando}>
                                        <Icon sx={{ mr: 0.5 }}>search</Icon>Buscar
                                    </MDButton>
                                </Grid>
                                <Grid item xs={12} sx={{ pt: "8px !important" }}>
                                    <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                        Robôs que rodam a cada poucos minutos só registram aqui as execuções com erro, com trabalho informado
                                        (processados, arquivos) ou que demoraram mais de 2 minutos; a última execução aparece sempre no cartão.
                                        O histórico é guardado por 90 dias.
                                    </MDTypography>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <MDBox height={4}>{carregando && <LinearProgress color="info" />}</MDBox>
                        {resultado && resultado.itens.length === 0 ? (
                            <MDBox py={4} textAlign="center">
                                <MDTypography variant="button" color="text">Nenhuma execução registrada com esses filtros.</MDTypography>
                            </MDBox>
                        ) : resultado && (
                            <TableContainer sx={{ boxShadow: "none", borderRadius: 0, background: "transparent" }}>
                                <Table sx={{ minWidth: 820 }}>
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            {["Início", "Duração", "Situação", "Processados", "Resumo / erro", "Arquivos", ""].map((t, i) => (
                                                <DataTableHeadCell key={i} sorted={false}>{t}</DataTableHeadCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {resultado.itens.map((e) => {
                                            const s = SITUACOES[e.situacao] || {};
                                            return (
                                                <TableRow key={e.id} hover sx={{ cursor: "pointer" }} onClick={() => setExecucaoAberta(e.id)}>
                                                    <TableCell sx={{ ...celula, ...bordaLinha, whiteSpace: "nowrap" }}>
                                                        <MDTypography variant="caption" color="text">{dataHora(e.inicio)}</MDTypography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha, whiteSpace: "nowrap" }}>
                                                        <MDTypography variant="caption" color="text">{formatarDuracao(e.duracao)}</MDTypography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha }}>
                                                        <Selo icone={s.icone || "help"} tom={s.tom} rotulo={e.situacaoLabel || "-"} />
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha }}>
                                                        <MDTypography variant="caption" color="text">{e.processados ?? "-"}</MDTypography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha, maxWidth: 420 }}>
                                                        <MDTypography variant="caption" color="text" sx={{ display: "block" }}>{e.resumo || (e.erroResumo ? "" : "-")}</MDTypography>
                                                        {e.erroResumo && (
                                                            <MDTypography variant="caption" sx={{ display: "block", color: cores.textoSecundario, fontStyle: "italic" }}>{e.erroResumo}</MDTypography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha }}>
                                                        <MDTypography variant="caption" color="text">{e.qtdArquivos > 0 ? e.qtdArquivos : "-"}</MDTypography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha, pt: 0.5 }}>
                                                        <Tooltip title="Ver execução">
                                                            <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); setExecucaoAberta(e.id); }}>
                                                                <Icon fontSize="small">open_in_new</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {resultado && resultado.total > POR_PAGINA && (
                            <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={1} pt={1}>
                                <MDTypography variant="caption" color="text">
                                    Página {pagina + 1} de {totalPaginas} · {resultado.total} execuções
                                </MDTypography>
                                <IconButton size="small" disabled={pagina === 0 || carregando} onClick={() => buscar(pagina - 1)}>
                                    <Icon>chevron_left</Icon>
                                </IconButton>
                                <IconButton size="small" disabled={pagina + 1 >= totalPaginas || carregando} onClick={() => buscar(pagina + 1)}>
                                    <Icon>chevron_right</Icon>
                                </IconButton>
                            </MDBox>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="info" onClick={onFechar}>Fechar</MDButton>
            </DialogActions>
            <DetalheExecucao id={execucaoAberta} onFechar={fecharExecucao} onErro={onErro} />
        </Dialog>
    );
}

// ------------------------------------------------------------------ aba Arquivos do WIBS

// Situações do controle dos arquivos enviados ao WIBS (tbRoboArquivoWibs, gravado pelo ral-robot)
const SITUACOES_WIBS = {
    FALHOU: { icone: "report", tom: "error", rotulo: "Parado" },
    AGUARDANDO_WIBS: { icone: "hourglass_top", tom: "warning", rotulo: "Aguardando o WIBS" },
    AGUARDANDO_EMAILS: { icone: "mark_email_unread", tom: "warning", rotulo: "Aguardando e-mails" },
    CONCLUIDO: { icone: "check_circle", tom: "success", rotulo: "Concluído" },
    DESCARTADO: { icone: "delete_outline", tom: null, rotulo: "Descartado" },
    SUBSTITUIDO: { icone: "call_merge", tom: null, rotulo: "Substituído" },
};
const SITUACAO_WIBS_OPTIONS = [
    { id: "", value: "", label: "Pendentes e parados + recentes" },
    { id: "PENDENTES", value: "PENDENTES", label: "Só pendentes e parados" },
    ...Object.entries(SITUACOES_WIBS).map(([value, s]) => ({ id: value, value, label: s.rotulo })),
];
const ROBO_WIBS_OPTIONS = [
    { id: "", value: "", label: "Todos" },
    { id: "RECORTES", value: "RECORTES", label: "Recortes" },
    { id: "EPROC", value: "EPROC", label: "EPROC" },
    { id: "TLEGAL", value: "TLEGAL", label: "T-Legal" },
    { id: "INTIMACOES", value: "INTIMACOES", label: "Intimações" },
];
const DIAS_WIBS_OPTIONS = [
    { id: 1, value: 1, label: "Hoje" },
    { id: 7, value: 7, label: "Últimos 7 dias" },
    { id: 30, value: 30, label: "Últimos 30 dias" },
    { id: 90, value: 90, label: "Últimos 90 dias" },
];
const ATUALIZAR_WIBS_MS = 60000;
// Chaves dos e-mails no controle (ControleArquivosWibs no ral-robot) em texto para a tela
const nomeDoEmail = (chave) => {
    if (chave === "DASHBOARD") return "Dashboard";
    if (chave === "NAO_LOCALIZADOS") return "Não localizados";
    const [tipo, ...resto] = String(chave).split(":");
    const nome = resto.join(":");
    return tipo === "GRUPO" || tipo === "EQUIPE" ? `Equipe ${nome}` : chave;
};
const horaMinuto = (data) => (data ? new Date(data).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "");
const letras = (texto) => (texto || "").replace(/[^\p{L}]/gu, "").length;

/** Tentar de novo ou descartar um arquivo do WIBS: confirmação (e o motivo, no descarte). */
function AcaoArquivoWibs({ acao, item, onFechar, onFeito }) {
    const [motivo, setMotivo] = useState("");
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState("");
    const corDoTom = useCorDoTom();
    if (!acao || !item) return null;
    const descartar = acao === "descartar";
    const naEtapaDoWibs = item.etapa !== "E-mails";
    const confirmar = () => {
        setEnviando(true);
        setErro("");
        post(`/robo-monitor/arquivos-wibs/${encodeURIComponent(item.id)}/${descartar ? "descartar" : "tentar-de-novo"}`, descartar ? { motivo } : {})
            .then(() => { onFeito(descartar ? "Arquivo descartado: o robô tira da pasta na próxima rodada." : "Na fila: o robô tenta de novo na próxima rodada."); })
            .catch((e) => setErro(e?.message || "Não foi possível concluir."))
            .finally(() => setEnviando(false));
    };
    return (
        <Dialog open onClose={enviando ? undefined : onFechar} maxWidth="sm" fullWidth>
            <DialogTitle>{descartar ? "Descartar o arquivo" : "Tentar de novo"}</DialogTitle>
            <DialogContent dividers>
                <MDTypography variant="button" color="text" component="div" fontWeight="bold" sx={{ wordBreak: "break-word" }}>
                    {item.roboNome}: {item.arquivo}
                </MDTypography>
                <MDTypography variant="caption" color="text" component="div" sx={{ mt: 1 }}>
                    {descartar ? (
                        <>
                            Na próxima rodada, o robô tira {item.arquivos.length > 1 ? "os arquivos" : "o arquivo"} da pasta para o
                            Nao_Processados, sem enviar ao WIBS, e não tenta mais. Use quando os andamentos já entraram no WIBS ou o
                            arquivo não deve ser processado.
                        </>
                    ) : naEtapaDoWibs ? (
                        <>
                            Na próxima rodada, o robô sobe o arquivo no WIBS de novo, com mais 3 tentativas.{" "}
                            <strong>O WIBS duplica os andamentos a cada envio</strong>: ele já recebeu este arquivo {item.enviosWibs} vez
                            {item.enviosWibs === 1 ? "" : "es"} sem devolver o retorno. Confira no WIBS se os andamentos já entraram antes
                            de tentar de novo.
                        </>
                    ) : (
                        <>
                            O WIBS já devolveu o retorno: na próxima rodada, o robô manda só os e-mails que faltaram, sem enviar ao
                            WIBS de novo (mais 3 tentativas).
                        </>
                    )}
                </MDTypography>
                {descartar && (
                    <MDBox mt={2}>
                        <InputLabel sx={rotulo}>Motivo *</InputLabel>
                        <MDInput size="small" fullWidth multiline minRows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Ex.: os andamentos já entraram no WIBS pelo envio das 08:30" />
                    </MDBox>
                )}
                {erro && <MDTypography variant="caption" component="div" sx={{ color: corDoTom("error"), mt: 1 }}>{erro}</MDTypography>}
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="info" onClick={onFechar} disabled={enviando}>Cancelar</MDButton>
                <MDButton variant="contained" color={descartar ? "error" : "info"} onClick={confirmar}
                    disabled={enviando || (descartar && letras(motivo) < 5)}>
                    {descartar ? "Descartar" : "Tentar de novo"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Push dos tribunais (e-mails) no topo da aba Arquivos do WIBS: os processos de hoje sem localização no WIBS (o que
 * ficou sem retorno, ex.: o Chrome que não abriu, e os não localizados) vão sozinhos no reprocessamento das 20h; "Enviar
 * agora" antecipa (o robô confere o pedido a cada minuto). O push não usa o controle dos arquivos (os e-mails ficam no banco).
 */
function PushHoje({ podeEditar, cores, corDoTom, onAviso }) {
    const [push, setPush] = useState(null);
    const [erro, setErro] = useState("");
    const [confirmar, setConfirmar] = useState(false);
    const [enviando, setEnviando] = useState(false);

    const carregar = useCallback(() => {
        get("/robo-monitor/push-hoje").then((p) => { setPush(p); setErro(""); })
            .catch((e) => setErro(e?.message || "Não foi possível ler o push de hoje."));
    }, []);
    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => {
        // Com pedido em aberto, confere mais vezes (o robô pega em até 1 min)
        const id = setInterval(() => { if (!document.hidden) carregar(); }, push?.pedidoEm ? 15000 : ATUALIZAR_WIBS_MS);
        return () => clearInterval(id);
    }, [carregar, push?.pedidoEm]);

    const enviar = () => {
        setEnviando(true);
        post("/robo-monitor/push-enviar-agora", {})
            .then(() => { setConfirmar(false); onAviso("Pedido enviado: o robô começa em até 1 minuto."); carregar(); })
            .catch((e) => setErro(e?.message || "Não foi possível pedir o envio."))
            .finally(() => setEnviando(false));
    };

    if (!push && !erro) return null;
    const pendentes = push?.semLocalizacao || 0;
    return (
        <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
            <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
                <MDBox minWidth={0} flex="1 1 360px">
                    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Icon sx={{ color: cores.textoSecundario }}>mark_email_read</Icon>
                        <MDTypography variant="button" fontWeight="bold" color="text">Push dos tribunais (e-mails) – hoje</MDTypography>
                        {push && (pendentes > 0
                            ? <Selo icone="hourglass_top" tom="warning" rotulo={`${pendentes} sem localização no WIBS`} />
                            : <Selo icone="check_circle" tom="success" rotulo="Nenhum pendente" />)}
                    </MDBox>
                    <MDTypography variant="caption" color="text" component="div" sx={{ mt: 0.5 }}>
                        Os processos de hoje que não foram localizados no WIBS (os que ficaram sem retorno, ex.: quando o Chrome do robô não abre,
                        e os que o WIBS não localizou) vão de novo ao WIBS e às equipes no reprocessamento das 20h. Enviar agora antecipa esse
                        reprocessamento; o e-mail de resumo dos não localizados continua só às 20h.
                    </MDTypography>
                    {push?.pedidoEm && (
                        <MDTypography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: "bold", color: corDoTom(push.pedidoEsquecido ? "error" : "info") }}>
                            {push.pedidoEsquecido
                                ? `O robô ainda não pegou o pedido de ${push.pedidoPor} às ${push.pedidoEm.slice(11)}: confira se o serviço dos robôs está no ar.`
                                : `Pedido de ${push.pedidoPor} às ${push.pedidoEm.slice(11)}: o robô começa em até 1 minuto.`}
                        </MDTypography>
                    )}
                    {push?.ultimoInicio && (
                        <MDTypography variant="caption" component="div" sx={{ mt: 0.5, color: cores.textoFraco }}>
                            Último Enviar agora: {dataHora(push.ultimoInicio)}{push.ultimoFim ? ` a ${horaMinuto(push.ultimoFim)}` : " (em andamento)"}
                            {push.ultimaSituacao ? ` · ${push.ultimaSituacao}` : ""}{push.ultimoResumo ? ` · ${push.ultimoResumo}` : ""}
                            {push.ultimoErro ? ` · ${push.ultimoErro.split("\n")[0]}` : ""}
                        </MDTypography>
                    )}
                    {push && !push.roboComEnviarAgora && (
                        <MDTypography variant="caption" component="div" sx={{ mt: 0.5, color: corDoTom("warning"), fontWeight: "bold" }}>
                            O robô deste ambiente ainda não tem o Enviar agora (precisa da versão nova do ral-robot no ar).
                        </MDTypography>
                    )}
                    {erro && <MDTypography variant="caption" component="div" sx={{ mt: 0.5, color: corDoTom("error") }}>{erro}</MDTypography>}
                </MDBox>
                {podeEditar && push && (
                    <MDButton variant="contained" color="info" size="medium" onClick={() => setConfirmar(true)}
                        disabled={!push.roboComEnviarAgora || pendentes === 0 || Boolean(push.pedidoEm)}>
                        <Icon sx={{ mr: 0.5 }}>send</Icon>Enviar agora
                    </MDButton>
                )}
            </MDBox>
            <Dialog open={confirmar} onClose={enviando ? undefined : () => setConfirmar(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Enviar agora os processos do push</DialogTitle>
                <DialogContent dividers>
                    <MDTypography variant="caption" color="text" component="div">
                        Em até 1 minuto, o robô manda ao WIBS os {pendentes} processo(s) de hoje sem localização no WIBS e envia às equipes os que
                        forem localizados (o mesmo reprocessamento das 20h, que continua rodando no horário com o que sobrar). Se o push estiver no
                        meio de uma rodada, ele espera ela terminar.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" component="div" sx={{ mt: 1 }}>
                        Os não localizados que o WIBS já devolveu vão de novo também (como às 20h): não duplicam andamento, porque o processo não
                        existe no WIBS.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton variant="text" color="info" onClick={() => setConfirmar(false)} disabled={enviando}>Cancelar</MDButton>
                    <MDButton variant="contained" color="info" onClick={enviar} disabled={enviando}>Enviar agora</MDButton>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

/**
 * Aba Arquivos do WIBS: o controle dos arquivos que os robôs enviam ao WIBS (Recortes, EPROC, T-Legal e Intimações).
 * O arquivo só sai da pasta quando o WIBS devolveu o retorno e todos os e-mails saíram; aqui se vê o que está pendente
 * ou parado e, com o Monitor em Editar (c083_U), se manda tentar de novo ou descartar (o robô faz na rodada seguinte).
 */
function ArquivosWibs({ podeEditar, onMudou }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const corDoTom = useCorDoTom();
    const [situacao, setSituacao] = useState(SITUACAO_WIBS_OPTIONS[0]);
    const [robo, setRobo] = useState(ROBO_WIBS_OPTIONS[0]);
    const [dias, setDias] = useState(DIAS_WIBS_OPTIONS[1]);
    const [lista, setLista] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState("");
    const [aviso, setAviso] = useState("");
    const [acao, setAcao] = useState(null);
    const [erroAberto, setErroAberto] = useState(null);

    const carregar = useCallback(() => {
        setCarregando(true);
        const params = new URLSearchParams({ dias: String(dias.value), situacao: situacao.value, robo: robo.value });
        get(`/robo-monitor/arquivos-wibs?${params.toString()}`)
            .then((l) => { setLista(l); setErro(""); })
            .catch((e) => setErro(e?.message || "Não foi possível carregar os arquivos do WIBS."))
            .finally(() => setCarregando(false));
    }, [dias, situacao, robo]);

    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => {
        const id = setInterval(() => { if (!document.hidden) carregar(); }, ATUALIZAR_WIBS_MS);
        return () => clearInterval(id);
    }, [carregar]);

    const itens = lista?.itens || [];
    const totais = lista?.totais || {};
    const agora = new Date();

    return (
        <Grid container spacing={2}>
            <MDSnackbar color="success" icon="check" title="Arquivos do WIBS" content={aviso} open={!!aviso}
                onClose={() => setAviso("")} close={() => setAviso("")} bgWhite />
            <Grid item xs={12}>
                <PushHoje podeEditar={podeEditar} cores={cores} corDoTom={corDoTom} onAviso={setAviso} />
            </Grid>
            <Grid item xs={12}>
                <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={6} md={4}>
                            <InputLabel sx={rotulo}>Situação</InputLabel>
                            <MDSelect name="situacaoWibs" value={situacao} options={SITUACAO_WIBS_OPTIONS} onChange={(v) => setSituacao(v || SITUACAO_WIBS_OPTIONS[0])} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <InputLabel sx={rotulo}>Robô</InputLabel>
                            <MDSelect name="roboWibs" value={robo} options={ROBO_WIBS_OPTIONS} onChange={(v) => setRobo(v || ROBO_WIBS_OPTIONS[0])} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <InputLabel sx={rotulo}>Período</InputLabel>
                            <MDSelect name="diasWibs" value={dias} options={DIAS_WIBS_OPTIONS} onChange={(v) => setDias(v || DIAS_WIBS_OPTIONS[1])} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <MDButton variant="contained" color="info" size="medium" fullWidth onClick={carregar} disabled={carregando}>
                                <Icon sx={{ mr: 0.5 }}>refresh</Icon>Atualizar
                            </MDButton>
                        </Grid>
                        <Grid item xs={12} sx={{ pt: "8px !important" }}>
                            <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                O arquivo só sai da pasta quando o WIBS devolveu o retorno e todos os e-mails saíram. Sem o retorno, o robô tenta
                                até 3 vezes, de 10 em 10 min (o WIBS duplica os andamentos a cada envio); com o retorno, um e-mail que não saiu é
                                mandado de novo sem subir o arquivo ao WIBS. Depois da 3ª, o arquivo fica parado na pasta e vai o aviso para os
                                Alertas do Monitor de Robôs. Os pendentes e parados aparecem sempre; o período vale para os outros.
                                {podeEditar ? "" : " Tentar de novo e descartar exigem o Monitor de Robôs com a opção Editar."}
                            </MDTypography>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <MDBox height={4}>{carregando && lista && <LinearProgress color="info" />}</MDBox>
            </Grid>
            {erro && (
                <Grid item xs={12}>
                    <MDTypography variant="caption" component="div" sx={{ color: corDoTom("error"), px: 1 }}>{erro}</MDTypography>
                </Grid>
            )}
            {!lista && !erro && (
                <Grid item xs={12}><MDBox display="flex" justifyContent="center"><CircularProgress color="info" size={28} /></MDBox></Grid>
            )}
            {lista?.semTabela && (
                <Grid item xs={12}>
                    <Card sx={{ p: 3, background: cores.cartao }}>
                        <MDTypography variant="button" color="text">
                            O controle dos arquivos do WIBS ainda não existe neste banco: ele nasce quando o robô com o controle roda pela
                            primeira vez (ou com o script V1_111).
                        </MDTypography>
                    </Card>
                </Grid>
            )}
            {lista && !lista.semTabela && (
                <Grid item xs={12}>
                    <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
                        <MDBox display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                            {Object.entries(SITUACOES_WIBS).filter(([s]) => totais[s]).map(([s, def]) => (
                                <Selo key={s} icone={def.icone} tom={def.tom} rotulo={`${def.rotulo}: ${totais[s]}`} />
                            ))}
                            {itens.length === 0 && (
                                <MDTypography variant="caption" color="text">Nenhum arquivo com esses filtros.</MDTypography>
                            )}
                        </MDBox>
                        {itens.length > 0 && (
                            <TableContainer sx={{ boxShadow: "none", borderRadius: 0, background: "transparent" }}>
                                <Table sx={{ minWidth: podeEditar ? 1180 : 1040 }}>
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            <DataTableHeadCell width="90px" sorted={false}>Robô</DataTableHeadCell>
                                            <DataTableHeadCell sorted={false}>Arquivo</DataTableHeadCell>
                                            <DataTableHeadCell width="190px" sorted={false}>Situação</DataTableHeadCell>
                                            <DataTableHeadCell width="200px" sorted={false}>Tentativas</DataTableHeadCell>
                                            <DataTableHeadCell width="260px" sorted={false}>Último erro</DataTableHeadCell>
                                            {podeEditar && <DataTableHeadCell width="150px" sorted={false} align="right">Ações</DataTableHeadCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {itens.map((i) => {
                                            const def = SITUACOES_WIBS[i.situacao] || { icone: "help", tom: null, rotulo: i.situacaoLabel };
                                            const aguardando = i.situacao === "AGUARDANDO_WIBS" || i.situacao === "AGUARDANDO_EMAILS";
                                            return (
                                                <TableRow key={i.id} hover>
                                                    <TableCell sx={celula}><MDTypography variant="caption" color="text" fontWeight="bold">{i.roboNome}</MDTypography></TableCell>
                                                    <TableCell sx={celula}>
                                                        {i.arquivos.map((nome) => (
                                                            <MDTypography key={nome} variant="caption" color="text" component="div" sx={{ wordBreak: "break-word" }}>{nome}</MDTypography>
                                                        ))}
                                                        <MDTypography variant="caption" component="div" sx={{ color: cores.textoFraco, wordBreak: "break-word" }}>
                                                            {i.pasta || "Gerado pelo robô (sem pasta)"} · desde {dataCurta(i.inicio, agora)}
                                                        </MDTypography>
                                                        {i.acoes.map((a, n) => (
                                                            <MDTypography key={`${a.data}-${n}`} variant="caption" component="div" sx={{ color: cores.textoFraco }}>
                                                                <Icon sx={iconeLinha}>history</Icon>{a.acao} por {a.usuario || "-"} em {dataHora(a.data)}{a.motivo ? `: ${a.motivo}` : ""}
                                                            </MDTypography>
                                                        ))}
                                                    </TableCell>
                                                    <TableCell sx={celula}>
                                                        <Selo icone={def.icone} tom={def.tom} rotulo={def.rotulo} />
                                                        {i.etapa && (
                                                            <MDTypography variant="caption" color="text" component="div" sx={{ mt: 0.5 }}>Etapa: {i.etapa}</MDTypography>
                                                        )}
                                                        {i.conclusao && (
                                                            <MDTypography variant="caption" color="text" component="div" sx={{ mt: 0.5 }}>em {dataCurta(i.conclusao, agora)}</MDTypography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={celula}>
                                                        <MDTypography variant="caption" color="text" component="div">
                                                            {i.enviosWibs} envio{i.enviosWibs === 1 ? "" : "s"} ao WIBS
                                                            {i.retornoWibs ? ` · retorno em ${dataCurta(i.retornoWibs, agora)}` : ""}
                                                        </MDTypography>
                                                        {(aguardando || i.situacao === "FALHOU") && (
                                                            <MDTypography variant="caption" color="text" component="div">
                                                                {i.situacao === "FALHOU"
                                                                    ? `Parou na ${i.tentativas}ª tentativa (${i.etapa === "E-mails" ? "e-mails" : "envio ao WIBS"})`
                                                                    : i.tentativas === 0 && !i.ultimaTentativa
                                                                        ? "Na fila: vai na próxima rodada do robô"
                                                                        : `${i.tentativas} de 3 tentativa${i.tentativas === 1 ? "" : "s"}${i.proximaTentativa ? ` · próxima às ${horaMinuto(i.proximaTentativa)}` : ""}`}
                                                            </MDTypography>
                                                        )}
                                                        {i.emailsEnviados.length > 0 && (
                                                            <MDTypography variant="caption" component="div" sx={{ color: cores.textoFraco, wordBreak: "break-word" }}>
                                                                E-mails que saíram: {i.emailsEnviados.map(nomeDoEmail).join(", ")}
                                                            </MDTypography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={celula}>
                                                        {i.ultimoErro ? (
                                                            <>
                                                                <MDTypography variant="caption" color="text" component="div" sx={{ wordBreak: "break-word" }}>
                                                                    {erroAberto === i.id || i.ultimoErro.length <= 160 ? i.ultimoErro : `${i.ultimoErro.slice(0, 160)}...`}
                                                                </MDTypography>
                                                                {i.ultimoErro.length > 160 && (
                                                                    <MDButton variant="text" color="info" size="small" sx={{ p: 0, minHeight: 0 }}
                                                                        onClick={() => setErroAberto(erroAberto === i.id ? null : i.id)}>
                                                                        {erroAberto === i.id ? "ver menos" : "ver tudo"}
                                                                    </MDButton>
                                                                )}
                                                            </>
                                                        ) : <MDTypography variant="caption" color="text">-</MDTypography>}
                                                    </TableCell>
                                                    {podeEditar && (
                                                        <TableCell sx={{ ...celula, whiteSpace: "nowrap" }} align="right">
                                                            <MDBox display="flex" flexDirection="column" alignItems="flex-end">
                                                                {i.podeTentarDeNovo && (
                                                                    <MDButton variant="text" color="info" size="small" onClick={() => setAcao({ tipo: "tentar", item: i })}>
                                                                        <Icon sx={{ mr: 0.5 }}>replay</Icon>Tentar de novo
                                                                    </MDButton>
                                                                )}
                                                                {i.podeDescartar && (
                                                                    <MDButton variant="text" color="error" size="small" onClick={() => setAcao({ tipo: "descartar", item: i })}>
                                                                        <Icon sx={{ mr: 0.5 }}>delete_outline</Icon>Descartar
                                                                    </MDButton>
                                                                )}
                                                            </MDBox>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>
                </Grid>
            )}
            {acao && (
                <AcaoArquivoWibs acao={acao.tipo === "descartar" ? "descartar" : "tentar"} item={acao.item} onFechar={() => setAcao(null)}
                    onFeito={(mensagem) => { setAcao(null); setAviso(mensagem); carregar(); if (onMudou) onMudou(); }} />
            )}
        </Grid>
    );
}

/**
 * Monitor de Robôs (Robôs > Monitor de Robôs, c083): situação de cada robô agendado no ral-robot, sinal de vida do
 * serviço, histórico de execuções e arquivos. Os alertas por e-mail vão para ROBO_MONITOR_EMAILS_ALERTA. A aba Arquivos
 * do WIBS mostra o controle dos arquivos que os robôs enviam ao WIBS (pendentes, parados, tentar de novo, descartar).
 */
function MonitorRobos() {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const [painel, setPainel] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [busca, setBusca] = useState("");
    const [grupo, setGrupo] = useState(GRUPO_TODOS);
    const [atualizacao, setAtualizacao] = useState(ATUALIZACAO_OPTIONS[2]);
    const [aberto, setAberto] = useState(null);
    const [erro, setErro] = useState("");
    const [caixaAberta, setCaixaAberta] = useState(false);
    const [aba, setAba] = useState(0);
    const [parados, setParados] = useState(0);
    const { userFunctionalities } = useUser();
    const podeReenviar = Boolean(userFunctionalities && userFunctionalities.includes(PERMISSAO_REENVIAR));

    // Arquivos parados no controle do WIBS: contados no nome da aba (sem a tabela ainda, nada)
    const contarParados = useCallback(() => {
        get("/robo-monitor/arquivos-wibs?situacao=FALHOU").then((l) => setParados(l?.itens?.length || 0)).catch(() => {});
    }, []);

    const carregar = useCallback(() => {
        setCarregando(true);
        contarParados();
        get("/robo-monitor/painel")
            .then(setPainel)
            .catch((e) => setErro(e?.message || "Não foi possível carregar o monitor."))
            .finally(() => setCarregando(false));
    }, [contarParados]);

    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => {
        if (!atualizacao.value) return undefined;
        const id = setInterval(carregar, atualizacao.value * 1000);
        return () => clearInterval(id);
    }, [atualizacao, carregar]);

    const robos = useMemo(() => ordenarRobos(painel?.robos), [painel]);
    const totais = useMemo(() => contarGrupos(robos), [robos]);
    const visiveis = robos.filter((r) => (!grupo.value || grupoDoFarol(r.farol) === grupo.value) && correspondeBusca(r, busca));
    // O robô aberto acompanha a atualização automática do painel
    const roboAberto = aberto ? robos.find((r) => r.chave === aberto) || null : null;
    const fecharRobo = useCallback(() => setAberto(null), []);
    const mostrarErro = useCallback((m) => setErro(m || "Erro."), []);
    const agora = painel?.agora;
    const corDaAba = (n) => (controller.darkMode ? (aba === n ? cores.texto : cores.textoSecundario) : "inherit");

    return (
        <MDBox pb={3}>
            <MDSnackbar color="error" icon="warning" title="Monitor de Robôs" content={erro} open={!!erro}
                onClose={() => setErro("")} close={() => setErro("")} bgWhite />
            <Card sx={{ px: 2, pt: 0.5, mb: 2, background: cores.cartao }}>
                <Tabs value={aba} onChange={(e, nova) => setAba(nova)} textColor={controller.darkMode ? "inherit" : "primary"} indicatorColor="info">
                    <Tab label="Robôs" sx={{ fontSize: 12, color: corDaAba(0) }} />
                    <Tab label={parados ? `Arquivos do WIBS (${parados} parado${parados === 1 ? "" : "s"})` : "Arquivos do WIBS"}
                        sx={{ fontSize: 12, color: corDaAba(1) }} />
                </Tabs>
            </Card>
            {aba === 1 && <ArquivosWibs podeEditar={podeReenviar} onMudou={contarParados} />}
            {aba === 0 && (
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {painel ? <Instancias instancias={painel.instancias || []} agora={agora} /> : (
                                <Card sx={{ p: 2, background: cores.cartao }}>
                                    <MDBox display="flex" justifyContent="center"><CircularProgress color="info" size={28} /></MDBox>
                                </Card>
                            )}
                        </Grid>

                        {Object.keys(GRUPOS).map((g) => (
                            <Grid item xs={12} sm={6} lg={3} key={g}>
                                <Indicador grupo={g} valor={painel ? totais[g] : "-"} selecionado={grupo.value === g}
                                    onClick={() => setGrupo(grupo.value === g ? GRUPO_TODOS : GRUPO_OPTIONS.find((o) => o.value === g))} />
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
                                <Grid container spacing={2} alignItems="flex-end">
                                    <Grid item xs={12} sm={6} md={4}>
                                        <InputLabel sx={rotulo}>Buscar</InputLabel>
                                        <MDInput size="small" fullWidth value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome do robô, máquina ou resumo" />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <InputLabel sx={rotulo}>Situação</InputLabel>
                                        <MDSelect name="grupo" value={grupo} options={GRUPO_OPTIONS} onChange={(v) => setGrupo(v || GRUPO_TODOS)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <InputLabel sx={rotulo}>Atualizar a cada</InputLabel>
                                        <MDSelect name="atualizacao" value={atualizacao} options={ATUALIZACAO_OPTIONS} onChange={(v) => setAtualizacao(v || ATUALIZACAO_OPTIONS[0])} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2}>
                                        <MDButton variant="contained" color="info" size="medium" fullWidth onClick={carregar} disabled={carregando}>
                                            <Icon sx={{ mr: 0.5 }}>refresh</Icon>Atualizar
                                        </MDButton>
                                    </Grid>
                                    <Grid item xs={12} sx={{ pt: "8px !important" }}>
                                        <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                            Clique em um robô para ver o histórico, os erros e os arquivos de cada execução (ex.: o CSV enviado ao WIBS e
                                            o retorno). Os alertas de erro, atraso, robô travado e serviço parado vão por e-mail para os endereços de
                                            Parametrização do Sistema &gt; E-mail - Alertas do Monitor de Robôs, uma vez por ocorrência e outra quando normaliza.
                                        </MDTypography>
                                    </Grid>
                                    {podeReenviar && (
                                        <Grid item xs={12} sx={{ pt: "8px !important" }}>
                                            <MDButton variant="outlined" color="info" size="small" onClick={() => setCaixaAberta(true)}>
                                                <Icon sx={{ mr: 0.5 }}>outgoing_mail</Icon>E-mails enviados pela caixa do sistema
                                            </MDButton>
                                            <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>
                                                Confira se um e-mail saiu (ex.: o lote do EPROC de uma equipe) e reenvie.
                                            </MDTypography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <MDBox height={4}>{carregando && painel && <LinearProgress color="info" />}</MDBox>
                        </Grid>
                        {painel && visiveis.length === 0 && (
                            <Grid item xs={12}>
                                <Card sx={{ p: 4, background: cores.cartao, textAlign: "center" }}>
                                    <MDTypography variant="button" color="text">
                                        {robos.length === 0
                                            ? "Nenhum robô registrado. Os robôs aparecem aqui quando o serviço dos robôs sobe com a versão do monitor."
                                            : "Nenhum robô com esses filtros."}
                                    </MDTypography>
                                </Card>
                            </Grid>
                        )}
                        {visiveis.map((r) => (
                            <Grid item xs={12} md={6} xl={4} key={r.chave}>
                                <CartaoRobo robo={r} agora={agora} onAbrir={() => setAberto(r.chave)} />
                            </Grid>
                        ))}
                    </Grid>
                    <DetalheRobo robo={roboAberto} agora={agora} onFechar={fecharRobo} onErro={mostrarErro} />
                    {podeReenviar && <EnviadosCaixaSistema aberto={caixaAberta} onFechar={() => setCaixaAberta(false)} />}
                </>
            )}
        </MDBox>
    );
}

export default MonitorRobos;
